<template>
  <VContainer
    class="justify-center pa-0"
    data-testid="connect"
  >
    <VRow v-if="!mediaOpen" no-gutters>
      <VCol cols="12" class="text-center mb-2">
        <SBtn
          text
          @click="show = !show"
        >
          <span class="text-initial pr-2" data-testid="expander-text">
            {{ expanderText }}
          </span>
          <VIcon>
            {{ icons[show ? 'mdiChevronUp' : 'mdiChevronDown'] }}
          </VIcon>
        </SBtn>
      </VCol>
    </VRow>

    <!-- Social Media -->
    <VExpandTransition>
      <VRow v-show="show" no-gutters data-testid="social-media">
        <VCol v-if="web" :cols="oneLine ? 6 : 12" class=" pb-6" :class="{ 'text-center': !oneLine }" data-testid="web">
          <SBtn :href="web" text target="_blank" rel="noreferrer" class="link-color text-capitalize">
            {{ $t('demonstrator.label.visitMyWebsite') }}
          </SBtn>
        </VCol>
        <VCol :cols="oneLine ? 6 : 12" :class="{ 'text-center': !oneLine }" class="pb-4">
          <DemonstratorSocialIcon
            :href="'mailto:'+email"
            :icon="icons.mdiEmail"
          />
          <DemonstratorSocialIcon
            :href="facebook"
            :icon="icons.mdiFacebook"
          />
          <DemonstratorSocialIcon
            :href="instagram"
            :icon="icons.mdiInstagram"
          />
          <DemonstratorSocialIcon
            :href="twitter"
            :icon="icons.mdiTwitter"
          />
          <DemonstratorSocialIcon
            :href="pinterest"
            :icon="icons.mdiPinterest"
          />
          <DemonstratorSocialIcon
            :href="youtube"
            :icon="icons.mdiYoutube"
          />
        </VCol>
      </VRow>
    </VExpandTransition>
  </VContainer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { mdiChevronUp, mdiChevronDown, mdiEmail, mdiFacebook, mdiInstagram, mdiPinterest, mdiTwitter, mdiYoutube } from '@mdi/js'
import type { Demonstrator } from '~/types/account'
import DemonstratorSocialIcon from '~/components/account/demonstrator/DemonstratorSocialIcon.vue'
import { useAccountStore } from '~/stores/account'

interface Props {
  demonstrator: Demonstrator
  mediaOpen?: boolean
  oneLine?: boolean
  connectText?: string
}

const props = withDefaults(defineProps<Props>(), {
  mediaOpen: false,
  oneLine: false,
  connectText: ''
})

const { $i18n } = useNuxtApp()

const icons = {
  mdiChevronUp,
  mdiChevronDown,
  mdiEmail,
  mdiFacebook,
  mdiInstagram,
  mdiPinterest,
  mdiTwitter,
  mdiYoutube
}

const accountStore = useAccountStore()
const socialMedia = ref<Demonstrator['socialMedia']>()
const show = ref(false)

const expanderText = computed(() => props.connectText ? props.connectText : ($i18n.t('demonstrator.label.connectWithMe')))

const web = computed(() => socialMedia.value?.web)
const email = computed(() => props.demonstrator?.email)
const facebook = computed(() => socialMedia.value?.facebook)
const instagram = computed(() => socialMedia.value?.instagram)
const twitter = computed(() => socialMedia.value?.twitter)
const pinterest = computed(() => socialMedia.value?.pinterest)
const youtube = computed(() => socialMedia.value?.youtube)

const updateDemonstratorMedia = async () => {
  socialMedia.value = await accountStore.fetchDemoSocialMedia(props.demonstrator)
}

watch(() => props.demonstrator, updateDemonstratorMedia, { immediate: true })

onMounted(() => {
  show.value = props.mediaOpen
})
</script>

<style lang="scss" scoped>
  .text-initial {
    text-transform: initial;
    font-weight: normal;
  }
  .link-color {
    color: rgb(var(--v-theme-legacyTeal));
  }
</style>
